import { select } from 'd3';
import * as yaml from 'js-yaml';
import { getConfig, defaultConfig } from '../../diagram-api/diagramAPI.js';
import type { DiagramDB } from '../../diagram-api/types.js';
import { log } from '../../logger.js';
import { isValidShape, type ShapeID } from '../../rendering-util/rendering-elements/shapes.js';
import type { ClusterShapeID } from '../../rendering-util/rendering-elements/clusters.js';
import type { Edge, Node } from '../../rendering-util/types.js';
import type { EdgeMetaData, NodeMetaData } from '../../types.js';
import utils, { getEdgeId } from '../../utils.js';
import common from '../common/common.js';
import {
  setAccTitle,
  getAccTitle,
  getAccDescription,
  setAccDescription,
  clear as commonClear,
  setDiagramTitle,
  getDiagramTitle,
} from '../common/commonDb.js';
import { createTooltip } from '../common/svgDrawCommon.js';
import type {
  AgentflowElementMapping,
  AgentflowSemanticModel,
  AgentflowStatementType,
  EdgeSemantic,
  ElementPosition,
  FlowClass,
  FlowEdge,
  FlowLink,
  FlowSubGraph,
  FlowText,
  FlowVertex,
  FlowVertexTypeParam,
  SemanticConnector,
  SemanticEdge,
  SemanticSubGraph,
  SemanticVertex,
  VertexKind,
} from './types.js';
import { AgentflowWarning } from './diagnostics.js';
import { normaliseNodeShapes, resolveShapeAlias } from './shapes.js';
import { assignColorSlots } from './colorSlots.js';
import type {
  AgentflowDiagnostic,
  AgentflowDiagnosticContext,
  AgentflowWarningId,
} from './diagnostics.js';

/**
 * Raw JISON `@$` location object. Produced by the jison-generated parser
 * when location tracking is enabled (default). Only the line/column/range
 * fields are used.
 */
interface JisonLocation {
  first_line: number;
  first_column: number;
  last_line: number;
  last_column: number;
  range?: [number, number];
}
import DOMPurify from 'dompurify';
interface LinkData {
  id: string;
}

const MERMAID_DOM_ID_PREFIX = 'agentflow-';

/**
 * Metadata keys that are presentation-only per the agentflow syntax specification §11 and
 * therefore stripped from the semantic model projection. Everything else
 * in `@{...}` metadata carries semantic weight and flows through.
 */
const SEMANTIC_METADATA_SKIP_KEYS = new Set([
  'shape',
  'view',
  'icon',
  'img',
  'form',
  'pos',
  'w',
  'h',
  'class',
  'style',
  'labelType',
]);

/**
 * Shape ids that mark a node as a tool definition per the agentflow syntax specification
 * §7: the canonical name plus the accepted aliases. v0.8.1 adds `tool` as
 * an alias. Membership in this set is the source of truth for
 * `isToolDefinition()`.
 */
const SUBROUTINE_ALIASES = new Set<string>([
  'subroutine',
  'subprocess',
  'subproc',
  'framed-rectangle',
  'tool',
]);

// Property-level metadata validation (§10 applicability, connector-ref
// resolution, §3.3 containment, reserved/synthetic ids) was removed from the
// parser in v0.8.2 (issue #64) — those checks are semantic concerns and now
// live in the semantics module. The parser keeps only structural parsing plus
// `shape` and `view` handling. The associated lookup tables were removed with
// the validators that consumed them.

/** Maps subgraph container types to their cluster shape IDs. */
const SUBGRAPH_TYPE_TO_SHAPE: Record<NonNullable<FlowSubGraph['type']>, ClusterShapeID> = {
  flow: 'flowGroup',
};

/**
 * Remove `__proto__` / `constructor` / `prototype` own keys from parsed `@{ }`
 * metadata, recursively.
 *
 * js-yaml defines `__proto__` with `Object.defineProperty` and the metadata
 * spreads copy it as an own property, so nothing is polluted inside mermaid.
 * But `metadata` and `getSemanticModel()` are consumer-facing, and a downstream
 * `merge(target, node.metadata)` over an authored `__proto__` key would
 * pollute. Strip it at the parse boundary so no consumer inherits the hazard.
 */
function stripPrototypeKeys<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((entry) => stripPrototypeKeys(entry)) as T;
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  const clean: Record<string, unknown> = {};
  for (const key of Object.keys(value)) {
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      continue;
    }
    clean[key] = stripPrototypeKeys((value as Record<string, unknown>)[key]);
  }
  return clean as T;
}

/**
 * Strip line-trailing commas from a multi-line `@{ … }` metadata body.
 *
 * Multi-line bodies are block YAML, where the comma-separated style of the
 * single-line form (`@{ a: 1, b: 2 }`) is a syntax error. Only commas that
 * are syntactically trailing get stripped: commas inside quoted scalars,
 * flow collections (`[ … ]` / `{ … }`) and block scalar content survive, and
 * a comma directly before a line-trailing comment counts as trailing.
 */
function stripLineTrailingCommas(body: string): string {
  let inSingle = false;
  let inDouble = false;
  let flowDepth = 0;
  let blockScalarIndent: number | undefined;
  const out: string[] = [];
  for (const line of body.split('\n')) {
    if (blockScalarIndent !== undefined) {
      const indent = line.length - line.trimStart().length;
      if (line.trim() === '' || indent > blockScalarIndent) {
        // Block scalar content is verbatim text — commas are content.
        out.push(line);
        continue;
      }
      blockScalarIndent = undefined;
    }
    let commentStart = -1;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inDouble) {
        if (ch === '\\') {
          i++;
        } else if (ch === '"') {
          inDouble = false;
        }
      } else if (inSingle) {
        if (ch === "'") {
          inSingle = false;
        }
      } else if (ch === '"') {
        inDouble = true;
      } else if (ch === "'") {
        inSingle = true;
      } else if (ch === '#' && (i === 0 || line[i - 1] === ' ' || line[i - 1] === '\t')) {
        commentStart = i;
        break;
      } else if (ch === '[' || ch === '{') {
        flowDepth++;
      } else if (ch === ']' || ch === '}') {
        flowDepth = Math.max(0, flowDepth - 1);
      }
    }
    if (inSingle || inDouble || flowDepth > 0) {
      // The line ends inside a quoted scalar or flow collection — any
      // trailing comma is content or a separator, not a stray.
      out.push(line);
      continue;
    }
    const code = commentStart >= 0 ? line.slice(0, commentStart) : line;
    const comment = commentStart >= 0 ? line.slice(commentStart) : '';
    if (/:\s*[>|][\d+-]*\s*$/.test(code)) {
      // `key: |` / `key: >` opens a block scalar; its deeper-indented lines
      // are verbatim content.
      blockScalarIndent = line.length - line.trimStart().length;
      out.push(line);
      continue;
    }
    out.push(code.replace(/,([\t ]*)$/, '$1') + comment);
  }
  return out.join('\n');
}

// We are using arrow functions assigned to class instance fields instead of methods as they are required by JISON
export class AgentFlowDB implements DiagramDB {
  private vertexCounter = 0;
  private config = getConfig();
  private vertices = new Map<string, FlowVertex>();
  private edges: FlowEdge[] & { defaultInterpolate?: string; defaultStyle?: string[] } = [];
  private classes = new Map<string, FlowClass>();
  private subGraphs: FlowSubGraph[] = [];
  private subGraphLookup = new Map<string, FlowSubGraph>();
  private globalNodes = new Set<string>();
  private connectors = new Map<string, FlowVertex>();
  private tooltips = new Map<string, string>();
  private subCount = 0;
  private firstGraphFlag = true;
  private direction: string | undefined;
  private version: string | undefined; // As in graph
  private secCount = -1;
  private posCrossRef: number[] = [];
  private diagramId = '';

  // ── Element-mapping infrastructure (PR 2a) ───────────────────────────
  // Agentflow reports source positions, so it opts into source-faithful
  // parsing: `Diagram.fromText` keeps `%%` comments in the parsed text and
  // hands over the frontmatter line offset, letting captured JISON `@$`
  // positions be reported in original-source space (including any YAML
  // frontmatter the parser never saw).
  public readonly preserveCommentsWhenParsing = true;
  private frontmatterLineOffset = 0;
  private elementMappings: AgentflowElementMapping[] = [];
  // Vertex mappings pushed for bare `id` references (no label/shape
  // brackets). A trailing `@{ ... }` block retypes these to 'attachment'
  // in `extendVertexMapping` (issue #75).
  private bareVertexMappings = new WeakSet<AgentflowElementMapping>();

  // ── Diagnostic layer (PR 2b) ──────────────────────────────────────────
  // Structured warnings/errors emitted during parse, post-parse validation,
  // and rendering. Consumed by conformance fixtures (issue #13) and by any
  // downstream tooling that wants programmatic access to problems the DB
  // found. `emitWarning` always also writes to `log.warn` so humans see
  // warnings in the console; tests assert against `getDiagnostics()`.
  private diagnostics: AgentflowDiagnostic[] = [];

  /**
   * Post-parse validators that emit diagnostics run at most once per parse
   * (idempotent `getData()` calls). Resettable via `clear()`.
   */
  private postParseValidationRun = false;

  // Functions to be run after graph rendering
  private funs: ((element: Element) => void)[] = []; // cspell:ignore funs

  constructor() {
    this.funs.push(this.setupToolTips.bind(this));

    // Needed for JISON since it only supports direct properties
    this.addVertex = this.addVertex.bind(this);
    this.firstGraph = this.firstGraph.bind(this);
    this.setDirection = this.setDirection.bind(this);
    this.addSubGraph = this.addSubGraph.bind(this);
    this.addGlobal = this.addGlobal.bind(this);
    this.addConnector = this.addConnector.bind(this);
    this.addConnectorMapping = this.addConnectorMapping.bind(this);
    this.addLink = this.addLink.bind(this);
    this.setLink = this.setLink.bind(this);
    this.updateLink = this.updateLink.bind(this);
    this.addClass = this.addClass.bind(this);
    this.setClass = this.setClass.bind(this);
    this.destructLink = this.destructLink.bind(this);
    this.setClickEvent = this.setClickEvent.bind(this);
    this.setTooltip = this.setTooltip.bind(this);
    this.updateLinkInterpolate = this.updateLinkInterpolate.bind(this);
    this.setClickFun = this.setClickFun.bind(this);
    this.bindFunctions = this.bindFunctions.bind(this);

    // Element-mapping hooks (see ./types.ts ElementPosition / AgentflowElementMapping)
    this.setFrontmatterLineOffset = this.setFrontmatterLineOffset.bind(this);
    this.addVertexMapping = this.addVertexMapping.bind(this);
    this.extendVertexMapping = this.extendVertexMapping.bind(this);
    this.addEdgeMapping = this.addEdgeMapping.bind(this);
    this.addSubgraphMapping = this.addSubgraphMapping.bind(this);

    // Diagnostic hooks (see ./diagnostics.ts)
    this.emitWarning = this.emitWarning.bind(this);
    this.emitError = this.emitError.bind(this);
    this.getDiagnostics = this.getDiagnostics.bind(this);

    this.lex = {
      firstGraph: this.firstGraph.bind(this),
    };

    this.clear();
    this.setGen('gen-2');
  }

  private sanitizeText(txt: string) {
    return common.sanitizeText(txt, this.config);
  }

  private sanitizeNodeLabelType(labelType?: string) {
    switch (labelType) {
      case 'markdown':
      case 'string':
      case 'text':
        return labelType;
      default:
        return 'markdown';
    }
  }

  /**
   * Sets the diagram's SVG element ID, used to prefix domIds for uniqueness
   * across multiple diagrams on the same page.
   */
  public setDiagramId(svgElementId: string) {
    this.diagramId = svgElementId;
  }

  /**
   * Function to lookup domId from id in the graph definition.
   * When diagramId is set, returns the prefixed version for DOM uniqueness.
   *
   * @param id - id of the node
   */
  public lookUpDomId(id: string) {
    for (const vertex of this.vertices.values()) {
      if (vertex.id === id) {
        return this.diagramId ? `${this.diagramId}-${vertex.domId}` : vertex.domId;
      }
    }
    return this.diagramId ? `${this.diagramId}-${id}` : id;
  }

  /**
   * Function called by parser when a node definition has been found
   */
  public addVertex(
    id: string,
    textObj: FlowText,
    type: FlowVertexTypeParam,
    style: string[],
    classes: string[],
    dir: string,
    props = {},
    metadata: any,
    metadataLoc?: JisonLocation
  ) {
    if (!id || id.trim().length === 0) {
      return;
    }
    // Extract the metadata from the shapeData, the syntax for adding metadata for nodes and edges is the same
    // so at this point we don't know if it's a node or an edge, but we can still extract the metadata
    let doc;
    if (metadata !== undefined) {
      let yamlData;
      // detect if shapeData contains a newline character
      if (!metadata.includes('\n')) {
        yamlData = '{\n' + metadata + '\n}';
      } else {
        yamlData = metadata + '\n';
      }
      try {
        // A whitespace-only YAML document loads as `null`, not `{}` — coalesce
        // so an empty multi-line `@{\n}` behaves like single-line `@{}` (whose
        // brace-wrap already yields `{}`) instead of handing `null` to the
        // metadata consumers below (issue #83).
        doc = (yaml.load(yamlData, { schema: yaml.JSON_SCHEMA }) ?? {}) as NodeMetaData;
      } catch (err) {
        // Multi-line bodies are block YAML, where the comma-separated style of
        // the single-line form is a syntax error. Retry once with line-trailing
        // commas stripped so trailing commas work in both forms; valid
        // documents never reach this path and are parsed verbatim above.
        const stripped = metadata.includes('\n') ? stripLineTrailingCommas(metadata) : metadata;
        if (stripped !== metadata) {
          try {
            doc = (yaml.load(stripped + '\n', { schema: yaml.JSON_SCHEMA }) ?? {}) as NodeMetaData;
          } catch {
            // The commas weren't the problem — surface the original error
            // against the original, unstripped buffer so positions match.
            this.rethrowMetadataYamlError(err, metadata, metadataLoc);
          }
        } else {
          // js-yaml reports the failure position relative to the `@{ ... }` block
          // buffer, not the source. Translate it into absolute source coordinates
          // before it propagates so error banners and editor markers land on the
          // right line/column (issue #56 part 2). Always throws.
          this.rethrowMetadataYamlError(err, metadata, metadataLoc);
        }
      }
      doc = stripPrototypeKeys(doc);
    }

    // Resolve shape alias (§4.3.2) before any other shape handling. Keep the
    // authored name around: the lowercase style rule in the vertex branch must
    // judge what the user typed, not an internal id like `roundedRect` that
    // alias resolution produces (`task` → `roundedRect` used to trip it).
    let authoredShape: string | undefined;
    if (doc && typeof (doc as unknown as Record<string, unknown>).shape === 'string') {
      const docRec = doc as unknown as Record<string, unknown>;
      authoredShape = docRec.shape as string;
      const resolved = resolveShapeAlias(authoredShape);
      if (resolved && resolved !== authoredShape) {
        docRec.shape = resolved;
      }
    }

    // Check if this is a subgraph (e.g. my_flow@{ view: "collapsed" })
    const subGraph = this.subGraphLookup.get(id);
    if (subGraph && doc) {
      subGraph.metadata = { ...subGraph.metadata, ...(doc as unknown as Record<string, unknown>) };
      return;
    }

    // Check if this is a connector declared via the `connector` keyword
    // — attach metadata to the connector record so applicability rules
    // (§10) apply on the connector kind.
    const connector = this.connectors.get(id);
    if (connector && doc) {
      connector.metadata = {
        ...connector.metadata,
        ...(doc as unknown as Record<string, unknown>),
      };
      return;
    }

    // Reserve 'connectors' as a declaration group ID for forward compat
    // (the synthesised connectors group was removed in v0.8.1, but the
    // id stays reserved). The subgraph branch above runs first, so a
    // user-declared `flow connectors[...]` still owns the id.
    if (id === 'connectors') {
      return;
    }

    // Check if this is an edge — route metadata to the edge instead of
    // creating a placeholder vertex. Presentation keys map to dedicated edge
    // fields; all authored metadata is carried faithfully onto `edge.metadata`
    // (which keys are valid on an edge is a semantic concern handled
    // downstream — issue #64).
    const edge = this.edges.find((e) => e.id === id);
    if (edge) {
      const edgeDoc = doc as unknown as EdgeMetaData & Record<string, unknown>;
      if (edgeDoc?.animate !== undefined) {
        edge.animate = edgeDoc.animate;
      }
      if (edgeDoc?.animation !== undefined) {
        edge.animation = edgeDoc.animation;
      }
      if (edgeDoc?.curve !== undefined) {
        edge.interpolate = edgeDoc.curve;
      }
      if (doc) {
        edge.metadata = { ...edge.metadata, ...(doc as unknown as Record<string, unknown>) };
      }
      return;
    }

    let txt;

    let vertex = this.vertices.get(id);
    if (vertex === undefined) {
      vertex = {
        id,
        labelType: 'text',
        domId: MERMAID_DOM_ID_PREFIX + id + '-' + this.vertexCounter,
        styles: [],
        classes: [],
      };
      this.vertices.set(id, vertex);
    }
    this.vertexCounter++;

    if (textObj !== undefined) {
      this.config = getConfig();
      txt = this.sanitizeText(textObj.text.trim());
      vertex.labelType = textObj.type;
      // strip quotes if string starts and ends with a quote
      if (txt.startsWith('"') && txt.endsWith('"')) {
        txt = txt.substring(1, txt.length - 1);
      }
      vertex.text = txt;
    } else {
      if (vertex.text === undefined) {
        vertex.text = id;
      }
    }
    if (type !== undefined) {
      vertex.type = type;
    }
    if (style !== undefined && style !== null) {
      style.forEach((s) => {
        vertex.styles.push(s);
      });
    }
    if (classes !== undefined && classes !== null) {
      classes.forEach((s) => {
        vertex.classes.push(s);
      });
    }
    if (dir !== undefined) {
      vertex.dir = dir;
    }
    if (vertex.props === undefined) {
      vertex.props = props;
    } else if (props !== undefined) {
      Object.assign(vertex.props, props);
    }

    if (doc) {
      vertex.metadata = { ...vertex.metadata, ...(doc as unknown as Record<string, unknown>) };
      if (doc.shape) {
        const authored = authoredShape ?? doc.shape;
        // An unknown shape stays a hard error, matching flowchart — accepting
        // something flowchart rejects is the divergence this grammar exists to
        // avoid. What it gains here is a source position, so an editor can
        // point at the `@{ … }` block instead of just blanking the diagram.
        //
        // A non-string value (e.g. `shape: 123`) is an ordinary unknown shape,
        // not an uncaught TypeError on `.toLowerCase` (issue #83).
        if (typeof authored !== 'string') {
          throw this.positionedShapeError(
            `No such shape: ${JSON.stringify(authored)}.`,
            metadataLoc
          );
        }
        if (authored !== authored.toLowerCase() || authored.includes('_')) {
          throw this.positionedShapeError(
            `No such shape: ${authored}. Shape names should be lowercase.`,
            metadataLoc
          );
        } else if (!isValidShape(doc.shape)) {
          throw this.positionedShapeError(`No such shape: ${doc.shape}.`, metadataLoc);
        }
        vertex.type = doc?.shape;
      }

      if (doc?.label) {
        // Sanitize like the bracket-label and edge-label paths do. Not
        // currently exploitable — `createText` sanitizes before writing and the
        // finished SVG is DOMPurify'd — but the metadata path should not be the
        // one place a label reaches the DB unsanitized.
        vertex.text = this.sanitizeText(doc.label);
        vertex.labelType = this.sanitizeNodeLabelType(doc?.labelType);
      }
      // `icon`, `img`, `form`, `pos`, `w` and `h` are deliberately not promoted
      // onto the vertex. Agentflow's shape catalogue (§4.3) is the six domain
      // shapes; the icon and image shapes these keys select are outside it, so
      // the shape they produced was replaced with the default and reported as
      // SHAPE_UNSUPPORTED before it ever reached the renderer. They stay
      // available as ordinary metadata on `vertex.metadata`.
    }
  }

  /**
   * Translate a js-yaml failure raised while parsing an `<id>@{ ... }` block
   * from block-relative coordinates into absolute source coordinates, then
   * rethrow (issue #56 part 2).
   *
   * js-yaml's `mark.line` / `mark.column` (and the `(R:C)` reference plus the
   * `N |` excerpt prefixes baked into `message`) count from the start of the
   * block buffer the DB hands it — either the synthetic `{\n … \n}` wrapper
   * (single-line `@{ … }`) or the verbatim multi-line body. Neither matches
   * the line the user sees. Using the `shapeData` symbol's JISON location
   * (`@{`'s position) plus the frontmatter offset, we map the reported
   * coordinate back to source space, rewrite the message's `(R:C)` and excerpt
   * prefixes, update `mark`, and attach a JISON-style `hash.loc` so downstream
   * tooling can read source coordinates structurally instead of re-deriving
   * them from the buffer.
   *
   * Defensive: if the error isn't a positioned js-yaml exception, or we lack
   * the block location, the original error propagates untouched — translation
   * never makes a failure harder to read than it already was.
   */
  /**
   * Build a shape error carrying a JISON-shaped `hash.loc`, so the position is
   * readable structurally rather than only as prose. Mirrors what
   * `rethrowMetadataYamlError` attaches for YAML failures.
   */
  private positionedShapeError(message: string, metadataLoc: JisonLocation | undefined): Error {
    const error = new Error(message);
    if (!metadataLoc) {
      return error;
    }
    const line = metadataLoc.first_line + this.frontmatterLineOffset;
    Object.assign(error, {
      hash: {
        text: '',
        token: null,
        line: line - 1,
        loc: {
          first_line: line,
          last_line: metadataLoc.last_line + this.frontmatterLineOffset,
          first_column: metadataLoc.first_column,
          last_column: metadataLoc.last_column,
        },
        expected: [],
      },
    });
    return error;
  }

  private rethrowMetadataYamlError(
    err: unknown,
    metadata: string,
    metadataLoc: JisonLocation | undefined
  ): never {
    const ex = err as {
      name?: string;
      reason?: string;
      message?: string;
      mark?: { line: number; column: number; buffer?: string; position?: number };
      hash?: unknown;
    };
    if (!ex || ex.name !== 'YAMLException' || !ex.mark || !metadataLoc) {
      throw err;
    }
    // Compute the whole translation into `translation` first, then commit it to
    // `ex` in one assignment-only block. If anything in the computation throws,
    // `translation` stays undefined and the original error is rethrown
    // untouched — never a half-rewritten one.
    let translation:
      | { message: string; markLine: number; markColumn: number; hash: unknown }
      | undefined;
    try {
      const isInline = !metadata.includes('\n');
      // Buffer the DB actually handed to yaml.load — kept in lock-step with the
      // `addVertex` wrapping above.
      const buffer = isInline ? '{\n' + metadata + '\n}' : metadata + '\n';
      // `@{`'s source position. Content begins two columns later (after `@{`).
      const blockLine = metadataLoc.first_line + this.frontmatterLineOffset;
      const contentColumn = metadataLoc.first_column + 2;

      // Map a 0-based (line, column) in the block buffer to 0-based source
      // coordinates. For the single-line wrapper only buffer line 1 holds real
      // content (lines 0 and 2 are the synthetic braces) and it lives on the
      // block's own source line. For a multi-line body, buffer line 0 is the
      // tail of the `@{` line and every later line is a verbatim source line,
      // so its columns already match source.
      const toSource = (line: number, column: number): { line: number; column: number } => {
        if (isInline) {
          return { line: blockLine, column: contentColumn + (line === 1 ? column : 0) };
        }
        return {
          line: blockLine + line,
          column: line === 0 ? contentColumn + column : column,
        };
      };

      const src = toSource(ex.mark.line, ex.mark.column);
      const reason =
        ex.reason ?? (ex.message ?? '').split('\n')[0].replace(/\s*\(\d+:\d+\)\s*$/, '');

      // Rebuild the excerpt in source-line space. Show the offending buffer
      // line (skipping the synthetic braces for the inline form) with a caret
      // under the failure column; tabs render as `→` exactly as js-yaml does so
      // the caret stays aligned.
      const bufferLines = buffer.split('\n');
      const display: number[] = [];
      if (isInline) {
        display.push(1);
      } else {
        for (let i = ex.mark.line - 1; i <= ex.mark.line + 1; i++) {
          if (i < 0 || i >= bufferLines.length) {
            continue;
          }
          // Drop the trailing empty line our `+ '\n'` appended.
          if (i === bufferLines.length - 1 && bufferLines[i] === '' && i !== ex.mark.line) {
            continue;
          }
          display.push(i);
        }
      }
      const gutterWidth = Math.max(...display.map((i) => String(toSource(i, 0).line).length));
      const snippet: string[] = [];
      for (const i of display) {
        const lineNo = toSource(i, 0).line;
        const prefix = ` ${String(lineNo).padStart(gutterWidth)} | `;
        snippet.push(prefix + bufferLines[i].replace(/\t/g, '→'));
        if (i === ex.mark.line) {
          snippet.push('-'.repeat(prefix.length + ex.mark.column) + '^');
        }
      }

      translation = {
        message: `${reason} (${src.line}:${src.column + 1})\n\n${snippet.join('\n')}`,
        markLine: src.line - 1,
        markColumn: src.column,
        // JISON-style hash so editors can consume source coordinates directly.
        hash: {
          text: '',
          token: null,
          line: src.line - 1,
          loc: {
            first_line: src.line,
            last_line: src.line,
            first_column: src.column,
            last_column: src.column + 1,
          },
          expected: [],
        },
      };
    } catch {
      // Translation failed — leave `ex` untouched and surface the original below.
    }

    if (translation) {
      ex.message = translation.message;
      ex.mark.line = translation.markLine;
      ex.mark.column = translation.markColumn;
      ex.hash = translation.hash;
    }
    throw ex;
  }

  /**
   * Function called by parser when a link/edge definition has been found
   *
   */
  public addSingleLink(_start: string, _end: string, type: any, id?: string) {
    const start = _start;
    const end = _end;

    const edge: FlowEdge = {
      start: start,
      end: end,
      type: undefined,
      text: '',
      labelType: 'text',
      classes: [],
      isUserDefinedId: false,
      interpolate: this.edges.defaultInterpolate,
    };
    log.info('abc78 Got edge...', edge);
    const linkTextObj = type.text;

    if (linkTextObj !== undefined) {
      edge.text = this.sanitizeText(linkTextObj.text.trim());

      // strip quotes if string starts and ends with a quote
      if (edge.text.startsWith('"') && edge.text.endsWith('"')) {
        edge.text = edge.text.substring(1, edge.text.length - 1);
      }
      edge.labelType = this.sanitizeNodeLabelType(linkTextObj.type);
    }

    if (type !== undefined) {
      edge.type = type.type;
      edge.stroke = type.stroke;
      edge.length = type.length > 10 ? 10 : type.length;
      if (type.edgeSemantic) {
        edge.edgeSemantic = type.edgeSemantic;
      }
    }
    if (id && !this.edges.some((e) => e.id === id)) {
      edge.id = id;
      edge.isUserDefinedId = true;
    } else {
      const existingLinks = this.edges.filter((e) => e.start === edge.start && e.end === edge.end);
      if (existingLinks.length === 0) {
        edge.id = getEdgeId(edge.start, edge.end, { counter: 0, prefix: 'L' });
      } else {
        edge.id = getEdgeId(edge.start, edge.end, {
          counter: existingLinks.length + 1,
          prefix: 'L',
        });
      }
    }

    if (this.edges.length < (this.config.maxEdges ?? 500)) {
      log.info('Pushing edge...');
      this.edges.push(edge);
    } else {
      throw new Error(
        `Edge limit exceeded. ${this.edges.length} edges found, but the limit is ${this.config.maxEdges}.

Initialize mermaid with maxEdges set to a higher number to allow more edges.
You cannot set this config via configuration inside the diagram as it is a secure config.
You have to call mermaid.initialize.`
      );
    }
  }

  private isLinkData(value: unknown): value is LinkData {
    return (
      value !== null &&
      typeof value === 'object' &&
      'id' in value &&
      typeof (value as LinkData).id === 'string'
    );
  }

  public addLink(_start: string[], _end: string[], linkData: unknown) {
    const id = this.isLinkData(linkData) ? linkData.id.replace('@', '') : undefined;

    log.info('addLink', _start, _end, id);

    // for a group syntax like A e1@--> B & C, only the first edge should have a userDefined id
    // the rest of the edges should have auto generated ids
    for (const start of _start) {
      for (const end of _end) {
        //use the id only for last node in _start and first node in _end
        const isLastStart = start === _start[_start.length - 1];
        const isFirstEnd = end === _end[0];
        if (isLastStart && isFirstEnd) {
          this.addSingleLink(start, end, linkData, id);
        } else {
          this.addSingleLink(start, end, linkData, undefined);
        }
      }
    }
  }

  /**
   * Updates a link's line interpolation algorithm
   */
  public updateLinkInterpolate(positions: ('default' | number)[], interpolate: string) {
    positions.forEach((pos) => {
      if (pos === 'default') {
        this.edges.defaultInterpolate = interpolate;
        return;
      }
      // Same bounds message as `updateLink`. Without it an out-of-range index
      // surfaced as `TypeError: Cannot set properties of undefined`.
      if (typeof pos === 'number' && pos >= this.edges.length) {
        throw new Error(
          `The index ${pos} for linkStyle is out of bounds. Valid indices for linkStyle are between 0 and ${
            this.edges.length - 1
          }. (Help: Ensure that the index is within the range of existing edges.)`
        );
      }
      this.edges[pos].interpolate = interpolate;
    });
  }

  /**
   * Updates a link with a style
   *
   */
  public updateLink(positions: ('default' | number)[], style: string[]) {
    positions.forEach((pos) => {
      if (typeof pos === 'number' && pos >= this.edges.length) {
        throw new Error(
          `The index ${pos} for linkStyle is out of bounds. Valid indices for linkStyle are between 0 and ${
            this.edges.length - 1
          }. (Help: Ensure that the index is within the range of existing edges.)`
        );
      }
      if (pos === 'default') {
        this.edges.defaultStyle = style;
      } else {
        this.edges[pos].style = style;
        // if edges[pos].style does have fill not set, set it to none
        if (
          (this.edges[pos]?.style?.length ?? 0) > 0 &&
          !this.edges[pos]?.style?.some((s) => s?.startsWith('fill'))
        ) {
          this.edges[pos]?.style?.push('fill:none');
        }
      }
    });
  }

  public addClass(ids: string, _style: string[]) {
    const style = _style
      .join()
      .replace(/\\,/g, '§§§')
      .replace(/,/g, ';')
      .replace(/§§§/g, ',')
      .split(';');
    ids.split(',').forEach((id) => {
      let classNode = this.classes.get(id);
      if (classNode === undefined) {
        classNode = { id, styles: [], textStyles: [] };
        this.classes.set(id, classNode);
      }

      if (style !== undefined && style !== null) {
        style.forEach((s) => {
          if (/color/.exec(s)) {
            const newStyle = s.replace('fill', 'bgFill'); // .replace('color', 'fill');
            classNode.textStyles.push(newStyle);
          }
          classNode.styles.push(s);
        });
      }
    });
  }

  /**
   * Called by parser when a graph definition is found, stores the direction of the chart.
   *
   */
  public setDirection(dir: string) {
    this.direction = dir.trim();

    if (/.*</.exec(this.direction)) {
      this.direction = 'RL';
    }
    if (/.*\^/.exec(this.direction)) {
      this.direction = 'BT';
    }
    if (/.*>/.exec(this.direction)) {
      this.direction = 'LR';
    }
    if (/.*v/.exec(this.direction)) {
      this.direction = 'TB';
    }
    if (this.direction === 'TD') {
      this.direction = 'TB';
    }
  }

  /**
   * Called by parser when a special node is found, e.g. a clickable element.
   *
   * @param ids - Comma separated list of ids
   * @param className - Class to add
   */
  public setClass(ids: string, className: string) {
    for (const id of ids.split(',')) {
      const vertex = this.vertices.get(id);
      if (vertex) {
        vertex.classes.push(className);
      }
      const edge = this.edges.find((e) => e.id === id);
      if (edge) {
        edge.classes.push(className);
      }
      const subGraph = this.subGraphLookup.get(id);
      if (subGraph) {
        subGraph.classes.push(className);
      }
    }
  }

  public setTooltip(ids: string, tooltip: string) {
    if (tooltip === undefined) {
      return;
    }
    tooltip = this.sanitizeText(tooltip);
    for (const id of ids.split(',')) {
      this.tooltips.set(this.version === 'gen-1' ? this.lookUpDomId(id) : id, tooltip);
    }
  }

  private setClickFun(id: string, functionName: string, functionArgs: string) {
    // if (_id[0].match(/\d/)) id = MERMAID_DOM_ID_PREFIX + id;
    if (getConfig().securityLevel !== 'loose') {
      return;
    }
    if (functionName === undefined) {
      return;
    }
    let argList: string[] = [];
    if (typeof functionArgs === 'string') {
      /* Splits functionArgs by ',', ignoring all ',' in double quoted strings */
      argList = functionArgs.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);
      for (let i = 0; i < argList.length; i++) {
        let item = argList[i].trim();
        /* Removes all double quotes at the start and end of an argument */
        /* This preserves all starting and ending whitespace inside */
        if (item.startsWith('"') && item.endsWith('"')) {
          item = item.substr(1, item.length - 2);
        }
        argList[i] = item;
      }
    }

    /* if no arguments passed into callback, default to passing in id */
    if (argList.length === 0) {
      argList.push(id);
    }

    const vertex = this.vertices.get(id);
    if (vertex) {
      vertex.haveCallback = true;
      this.funs.push(() => {
        // Defer lookUpDomId to bind time so it includes the diagramId prefix
        const domId = this.lookUpDomId(id);
        const elem = document.querySelector(`[id="${domId}"]`);
        if (elem !== null) {
          elem.addEventListener(
            'click',
            () => {
              utils.runFunc(functionName, ...argList);
            },
            false
          );
        }
      });
    }
  }

  /**
   * Called by parser when a link is found. Adds the URL to the vertex data.
   *
   * @param ids - Comma separated list of ids
   * @param linkStr - URL to create a link for
   * @param target - Target attribute for the link
   */
  public setLink(ids: string, linkStr: string, target: string) {
    ids.split(',').forEach((id) => {
      const vertex = this.vertices.get(id);
      if (vertex !== undefined) {
        vertex.link = utils.formatUrl(linkStr, this.config);
        vertex.linkTarget = target;
      }
    });
    this.setClass(ids, 'clickable');
  }

  public getTooltip(id: string) {
    return this.tooltips.get(id);
  }

  /**
   * Called by parser when a click definition is found. Registers an event handler.
   *
   * @param ids - Comma separated list of ids
   * @param functionName - Function to be called on click
   * @param functionArgs - Arguments to be passed to the function
   */
  public setClickEvent(ids: string, functionName: string, functionArgs: string) {
    ids.split(',').forEach((id) => {
      this.setClickFun(id, functionName, functionArgs);
    });
    this.setClass(ids, 'clickable');
  }

  public bindFunctions(element: Element) {
    this.funs.forEach((fun) => {
      fun(element);
    });
  }
  public getDirection() {
    return this.direction?.trim();
  }
  /**
   * Retrieval function for fetching the found nodes after parsing has completed.
   *
   */
  public getVertices() {
    return this.vertices;
  }

  /**
   * Retrieval function for fetching the found links after parsing has completed.
   *
   */
  public getEdges() {
    return this.edges;
  }

  /**
   * Returns true when `vertex` is a **tool definition** per
   * the agentflow syntax specification §8 — its resolved shape is `subroutine` or one
   * of the accepted aliases (`subprocess`, `subproc`, `framed-rectangle`).
   *
   * This is the source of truth for "is this a tool?" — there is no
   * separate kind tag stored on the vertex; tool-ness is derived from
   * shape on every check. Downstream consumers reading the semantic model
   * see this surfaced as `vertexKind: 'tool'` (see `getSemanticModel`).
   */
  public isToolDefinition(vertex: FlowVertex): boolean {
    return SUBROUTINE_ALIASES.has(vertex.type as string);
  }

  /**
   * Returns every vertex that is a tool definition (per `isToolDefinition`).
   * Derived view; not cached.
   */
  public getTools(): FlowVertex[] {
    const tools: FlowVertex[] = [];
    for (const vertex of this.vertices.values()) {
      if (this.isToolDefinition(vertex)) {
        tools.push(vertex);
      }
    }
    return tools;
  }

  /**
   * Retrieval function for fetching the found class definitions after parsing has completed.
   *
   */
  public getClasses() {
    return this.classes;
  }

  private setupToolTips(element: Element) {
    const tooltipElem = createTooltip();

    const svg = select(element).select('svg');

    const nodes = svg.selectAll('g.node');
    nodes
      .on('mouseover', (e: MouseEvent) => {
        const el = select(e.currentTarget as Element);
        const title = el.attr('title');

        // Don't try to draw a tooltip if no data is provided
        if (title === null) {
          return;
        }
        const rect = (e.currentTarget as Element)?.getBoundingClientRect();

        tooltipElem.transition().duration(200).style('opacity', '.9');
        tooltipElem
          .text(el.attr('title'))
          .style('left', window.scrollX + rect.left + (rect.right - rect.left) / 2 + 'px')
          .style('top', window.scrollY + rect.bottom + 'px');
        tooltipElem.html(DOMPurify.sanitize(title));
        el.classed('hover', true);
      })
      .on('mouseout', (e: MouseEvent) => {
        tooltipElem.transition().duration(500).style('opacity', 0);
        const el = select(e.currentTarget as Element);
        el.classed('hover', false);
      });
  }

  /**
   * Clears the internal graph db so that a new graph can be parsed.
   *
   */
  public clear(ver = 'gen-2') {
    this.vertices = new Map();
    this.classes = new Map();
    this.edges = [];
    this.funs = [this.setupToolTips.bind(this)];
    this.subGraphs = [];
    this.subGraphLookup = new Map();
    this.globalNodes = new Set();
    this.connectors = new Map();
    this.subCount = 0;
    this.tooltips = new Map();
    this.firstGraphFlag = true;
    this.version = ver;
    this.config = getConfig();
    this.diagramId = '';
    this.vertexCounter = 0;
    this.direction = undefined;
    this.secCount = -1;
    this.posCrossRef = [];
    this.frontmatterLineOffset = 0;
    this.elementMappings = [];
    this.bareVertexMappings = new WeakSet();
    this.diagnostics = [];
    this.postParseValidationRun = false;
    commonClear();
  }

  public setGen(ver: string) {
    this.version = ver || 'gen-2';
  }

  /**
   * Legacy `linkStyle default` colours, byte-identical to `flowDb`. Hardcoded
   * rather than theme-derived on purpose: nothing in the repo calls this, and
   * changing the values would diverge from flowchart for no visible gain.
   */
  public defaultStyle() {
    return 'fill:#ffa;stroke: #f66; stroke-width: 3px; stroke-dasharray: 5, 5;fill:#ffa;stroke: #666;';
  }

  public addSubGraph(
    _id: { text: string },
    list: string[],
    _title: { text: string; type: string },
    type?: 'flow'
  ) {
    let id: string | undefined = _id?.text?.trim();
    let title = _title?.text;
    if (_id === _title && _title?.text && /\s/.exec(_title.text)) {
      id = undefined;
    }

    const uniq = (a: any[]) => {
      const prims: any = { boolean: {}, number: {}, string: {} };
      const objs: any[] = [];

      let dir: string | undefined;

      const nodeList = a.filter(function (item) {
        const type = typeof item;
        if (item.stmt && item.stmt === 'dir') {
          dir = item.value;
          return false;
        }
        if (item.trim() === '') {
          return false;
        }
        if (type in prims) {
          return prims[type].hasOwnProperty(item) ? false : (prims[type][item] = true);
        } else {
          return objs.includes(item) ? false : objs.push(item);
        }
      });
      return { nodeList, dir };
    };

    const result = uniq(list.flat());
    const nodeList = result.nodeList;
    let dir = result.dir;
    const flowchartConfig = getConfig().flowchart ?? {};
    dir =
      dir ??
      (flowchartConfig.inheritDir
        ? (this.getDirection() ?? (getConfig() as any).direction ?? undefined)
        : undefined);

    if (this.version === 'gen-1') {
      for (let i = 0; i < nodeList.length; i++) {
        nodeList[i] = this.lookUpDomId(nodeList[i]);
      }
    }

    id = id ?? 'subGraph' + this.subCount;
    title = title || '';
    title = this.sanitizeText(title);
    this.subCount = this.subCount + 1;

    const subGraph: FlowSubGraph = {
      id: id,
      // A subgraph is never a member of itself. In-block edge endpoints are
      // folded into the member list, so `a --> myFlow` written inside
      // `flow myFlow … end` would otherwise make the flow its own member and
      // self-parent, crashing the renderer with graphlib's "would create a
      // cycle" (issue #70). Globally scoped nodes (`global … end`, issue #80)
      // are exempt from membership the same way — referencing one inside a
      // flow must not adopt it.
      nodes: nodeList.filter((nodeId: string) => nodeId !== id && !this.globalNodes.has(nodeId)),
      title: title.trim(),
      classes: [],
      dir,
      labelType: this.sanitizeNodeLabelType(_title?.type),
      type: type ?? 'flow',
    };

    log.info('Adding', subGraph.id, subGraph.nodes, subGraph.dir);

    // Remove the members in the new subgraph if they already belong to another subgraph
    subGraph.nodes = this.makeUniq(subGraph, this.subGraphs).nodes;

    // Duplicate subgraph IDs merge children into the first occurrence
    // (structural). Identifier-namespace diagnostics moved to semantics (#64).
    const existingPos = this.getPosForId(id);
    if (existingPos !== -1) {
      // Duplicate subgraph ID — merge children into the existing entry.
      // First occurrence wins for hierarchy position.
      const existing = this.subGraphs[existingPos];
      for (const nodeId of subGraph.nodes) {
        if (!existing.nodes.includes(nodeId)) {
          existing.nodes.push(nodeId);
        }
      }
      // Last occurrence wins for properties
      if (subGraph.title) {
        existing.title = subGraph.title;
      }
      if (subGraph.dir) {
        existing.dir = subGraph.dir;
      }
      if (subGraph.type) {
        existing.type = subGraph.type;
      }
      this.subGraphLookup.set(id, existing);
    } else {
      this.subGraphs.push(subGraph);
      this.subGraphLookup.set(id, subGraph);
    }
    return id;
  }

  /**
   * Registers every node id that appears inside a `global … end` block as
   * globally scoped (issue #80): the node keeps no parent even when it is
   * referenced inside a `flow … end` block, opting out of the textual
   * membership rule that would otherwise pull it in. Edges declared inside
   * the block are ordinary top-level edges — only the membership exemption
   * is recorded here, and no container is emitted for the block.
   */
  public addGlobal(list: unknown[]) {
    for (const item of list.flat()) {
      // Skip non-id statement values (e.g. `direction` markers); node ids
      // arrive as plain strings.
      if (typeof item !== 'string') {
        continue;
      }
      const id = item.trim();
      if (id !== '') {
        this.globalNodes.add(id);
      }
    }
    // Flows parsed before this block already claimed the ids — release them
    // so the exemption is independent of declaration order.
    for (const subGraph of this.subGraphs) {
      subGraph.nodes = subGraph.nodes.filter((nodeId) => !this.globalNodes.has(nodeId));
    }
  }

  private getPosForId(id: string) {
    for (const [i, subGraph] of this.subGraphs.entries()) {
      if (subGraph.id === id) {
        return i;
      }
    }
    return -1;
  }

  private indexNodes2(id: string, pos: number): { result: boolean; count: number } {
    const nodes = this.subGraphs[pos].nodes;
    this.secCount = this.secCount + 1;
    if (this.secCount > 2000) {
      return {
        result: false,
        count: 0,
      };
    }
    this.posCrossRef[this.secCount] = pos;
    // Check if match
    if (this.subGraphs[pos].id === id) {
      return {
        result: true,
        count: 0,
      };
    }

    let count = 0;
    let posCount = 1;
    while (count < nodes.length) {
      const childPos = this.getPosForId(nodes[count]);
      // Ignore regular nodes (pos will be -1)
      if (childPos >= 0) {
        const res = this.indexNodes2(id, childPos);
        if (res.result) {
          return {
            result: true,
            count: posCount + res.count,
          };
        } else {
          posCount = posCount + res.count;
        }
      }
      count = count + 1;
    }

    return {
      result: false,
      count: posCount,
    };
  }

  public getDepthFirstPos(pos: number) {
    return this.posCrossRef[pos];
  }
  public indexNodes() {
    this.secCount = -1;
    if (this.subGraphs.length > 0) {
      this.indexNodes2('none', this.subGraphs.length - 1);
    }
  }

  /**
   * Called by parser when a `connector <id>["Title"]` declaration is
   * encountered (§8). Stores the connector as a FlowVertex with
   * `isConnector: true` so the connector flows through the renderer
   * alongside vertices, while keeping a separate index for connector-
   * specific lookups (e.g. `connectorRef` resolution).
   */
  public addConnector(textObj: FlowText, titleObj?: FlowText): string {
    const id = textObj?.text?.trim();
    if (!id) {
      return '';
    }

    const title = titleObj?.text?.trim() ?? '';
    const resolvedTitle = title.length > 0 ? this.sanitizeText(title) : id;

    let connector = this.connectors.get(id);
    if (!connector) {
      connector = {
        id,
        labelType: 'text',
        domId: MERMAID_DOM_ID_PREFIX + id + '-' + this.vertexCounter,
        styles: [],
        classes: [],
        isConnector: true,
        text: resolvedTitle,
      };
      this.connectors.set(id, connector);
      this.vertices.set(id, connector);
      this.vertexCounter++;
    } else if (title.length > 0) {
      connector.text = resolvedTitle;
    }
    return id;
  }

  /**
   * Live array. `getData()` and the JISON actions read it on the hot path and a
   * per-call copy would be wasted work; unlike `getDiagnostics()` this is not an
   * accessor built for external consumers. Same for `getVertices()` /
   * `getEdges()`, whose `defaultStyle` / `defaultInterpolate` side-properties a
   * copy would drop.
   */
  public getSubGraphs() {
    return this.subGraphs;
  }

  /** Returns connectors declared via the `connector` keyword (§8). */
  public getConnectors(): FlowVertex[] {
    return [...this.connectors.values()];
  }

  public firstGraph() {
    if (this.firstGraphFlag) {
      this.firstGraphFlag = false;
      return true;
    }
    return false;
  }

  /**
   * Maps the post-`destructLink` `(type, stroke)` pair onto the canonical
   * `edgeSemantic` value defined by the agentflow syntax specification §5.1 (v0.8.1).
   * The three-way table:
   *   - `-->` → arrow_point + normal → sequence
   *   - `-.-` → arrow_open  + dotted → reference
   *   - `--x` → arrow_cross + normal → failure
   * Returns `undefined` for combinations the spec no longer permits.
   */
  private computeEdgeSemantic(
    type: string | undefined,
    stroke: 'normal' | 'thick' | 'invisible' | 'dotted' | undefined
  ): EdgeSemantic | undefined {
    if (type === 'arrow_point' && stroke === 'normal') {
      return 'sequence';
    }
    if (type === 'arrow_cross' && stroke === 'normal') {
      return 'failure';
    }
    if (type === 'arrow_open' && stroke === 'dotted') {
      return 'reference';
    }
    return undefined;
  }

  private destructStartLink(_str: string): FlowLink {
    const str = _str.trim();
    // v0.8.1: only `-->`, `-.-`, `--x` are valid. Start-link forms (the
    // first half of a split arrow `a -- xyz -->`) only carry stroke/dot
    // information; the type comes from the end-link.
    const type = 'arrow_open';

    let stroke = 'normal';
    if (str.includes('.')) {
      stroke = 'dotted';
    }

    return { type, stroke };
  }

  private countChar(char: string, str: string) {
    const length = str.length;
    let count = 0;
    for (let i = 0; i < length; ++i) {
      if (str[i] === char) {
        ++count;
      }
    }
    return count;
  }

  private destructEndLink(_str: string) {
    const str = _str.trim();
    let line = str.slice(0, -1);
    let type = 'arrow_open';

    switch (str.slice(-1)) {
      case 'x':
        type = 'arrow_cross';
        break;
      case '>':
        type = 'arrow_point';
        break;
      case '-':
      case '.':
        // `-.-` form: the trailing character is `-` (or `.`), the operator
        // is non-directional. Keep `arrow_open` so the renderer draws no
        // arrowhead and we route to the reference semantic.
        type = 'arrow_open';
        line = str;
        break;
    }

    let stroke: 'normal' | 'thick' | 'invisible' | 'dotted' = 'normal';
    let length = line.length - 1;

    const dots = this.countChar('.', line);
    if (dots > 0) {
      stroke = 'dotted';
      length = dots;
    }

    return { type, stroke, length };
  }

  public destructLink(_str: string, _startStr: string) {
    const info = this.destructEndLink(_str);
    let startInfo;
    if (_startStr) {
      startInfo = this.destructStartLink(_startStr);

      if (startInfo.stroke !== info.stroke) {
        return { type: 'INVALID', stroke: 'INVALID' };
      }

      // -- xyz -->  - take arrow type from ending
      startInfo.type = info.type;
      startInfo.length = info.length;
      return {
        ...startInfo,
        edgeSemantic: this.computeEdgeSemantic(
          startInfo.type,
          startInfo.stroke as 'normal' | 'thick' | 'invisible' | 'dotted'
        ),
      };
    }

    return {
      ...info,
      edgeSemantic: this.computeEdgeSemantic(info.type, info.stroke),
    };
  }

  // Todo optimizer this by caching existing nodes
  public exists(allSgs: FlowSubGraph[], _id: string) {
    for (const sg of allSgs) {
      if (sg.nodes.includes(_id)) {
        return true;
      }
    }
    return false;
  }
  /**
   * Deletes an id from all subgraphs
   *
   */
  public makeUniq(sg: FlowSubGraph, allSubgraphs: FlowSubGraph[]) {
    const res: string[] = [];
    sg.nodes.forEach((_id, pos) => {
      if (!this.exists(allSubgraphs, _id)) {
        res.push(sg.nodes[pos]);
      }
    });
    return { nodes: res };
  }

  public lex: { firstGraph: typeof AgentFlowDB.prototype.firstGraph };

  private getTypeFromVertex(vertex: FlowVertex): ShapeID {
    // Resolve v0.8.1 alias (e.g. `task` → `roundedRect`, `action` → `hexagon`)
    // before mapping to the canonical Mermaid shape id.
    const resolved = typeof vertex.type === 'string' ? resolveShapeAlias(vertex.type) : vertex.type;
    switch (resolved) {
      case 'square':
      case undefined:
        return 'squareRect';
      case 'round':
        return 'roundedRect';
      case 'ellipse':
        // @ts-expect-error -- Ellipses are broken, see https://github.com/mermaid-js/mermaid/issues/5976
        return 'ellipse';
      default:
        return resolved as ShapeID;
    }
  }

  private findNode(nodes: Node[], id: string) {
    return nodes.find((node) => node.id === id);
  }
  private destructEdgeType(type: string | undefined) {
    let arrowTypeStart = 'none';
    let arrowTypeEnd = 'arrow_point';
    switch (type) {
      case 'arrow_point':
      case 'arrow_circle':
      case 'arrow_cross':
      case 'arrow_hierarchy':
        arrowTypeEnd = type;
        break;

      case 'double_arrow_point':
      case 'double_arrow_circle':
      case 'double_arrow_cross':
        arrowTypeStart = type.replace('double_', '');
        arrowTypeEnd = arrowTypeStart;
        break;
    }
    return { arrowTypeStart, arrowTypeEnd };
  }

  private addNodeFromVertex(
    vertex: FlowVertex,
    nodes: Node[],
    parentDB: Map<string, string>,
    subGraphDB: Map<string, boolean>,
    config: any,
    look: string
  ) {
    const parentId = parentDB.get(vertex.id);
    const isGroup = subGraphDB.get(vertex.id) ?? false;

    const node = this.findNode(nodes, vertex.id);
    if (node) {
      node.cssStyles = vertex.styles;
      node.cssCompiledStyles = this.getCompiledStyles(vertex.classes);
      node.cssClasses = vertex.classes.join(' ');
    } else {
      const baseNode = {
        id: vertex.id,
        label: vertex.text,
        labelType: vertex.labelType,
        labelStyle: '',
        parentId,
        padding: config.flowchart?.padding || 8,
        cssStyles: vertex.styles,
        cssCompiledStyles: this.getCompiledStyles(['default', 'node', ...vertex.classes]),
        cssClasses: 'default ' + vertex.classes.join(' '),
        dir: vertex.dir,
        domId: vertex.domId,
        look,
        link: vertex.link,
        linkTarget: vertex.linkTarget,
        tooltip: this.getTooltip(vertex.id),
        metadata: vertex.metadata,
      };
      if (isGroup) {
        nodes.push({
          ...baseNode,
          isGroup: true,
          shape: 'rect',
        });
      } else {
        nodes.push({
          ...baseNode,
          isGroup: false,
          shape: this.getTypeFromVertex(vertex),
          wrappingWidth: config.agentflow?.wrappingWidth,
          minWidth: config.agentflow?.minNodeWidth,
        });
      }
    }
  }

  private getCompiledStyles(classDefs: string[]) {
    let compiledStyles: string[] = [];
    for (const customClass of classDefs) {
      const cssClass = this.classes.get(customClass);
      if (cssClass?.styles) {
        compiledStyles = [...compiledStyles, ...(cssClass.styles ?? [])].map((s) => s.trim());
      }
      if (cssClass?.textStyles) {
        compiledStyles = [...compiledStyles, ...(cssClass.textStyles ?? [])].map((s) => s.trim());
      }
    }
    return compiledStyles;
  }

  /**
   * Post-parse hook. Property-level metadata validation and semantic checks
   * (§10 applicability, connector-ref resolution, §3.3 containment, edge
   * endpoint kinds, flow input, identifier namespace) were removed from the
   * parser in v0.8.2 (issue #64) — they are semantic concerns owned by the
   * semantics module. The parser keeps only structural parsing plus `shape`
   * (unknown-shape errors, see `transformData`) and `view` handling. This hook
   * remains as a no-op so downstream callers and the run-once guard are intact.
   */
  private runPostParseValidators(): void {
    if (this.postParseValidationRun) {
      return;
    }
    this.postParseValidationRun = true;
  }

  public getData() {
    this.runPostParseValidators();
    const config = getConfig();
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    const subGraphs = this.getSubGraphs();
    const parentDB = new Map<string, string>();
    const subGraphDB = new Map<string, boolean>();

    // Collect IDs hidden by collapsed subgraphs (the subgraph's descendants).
    // `collapsedAncestorMap` records the *outermost* collapsed ancestor for
    // each hidden id so that cross-boundary edges can be redirected to the
    // visible collapsed node (issue #53). Nested collapses resolve to the
    // outermost ancestor because we process collapsed subgraphs in
    // declaration order (outer first) and the ancestor parameter is held
    // constant during recursion.
    const hiddenIds = new Set<string>();
    const collapsedAncestorMap = new Map<string, string>();
    //
    // Containment can be cyclic: an edge written inside one container that
    // names another container folds that container into the first one's member
    // list, so `A` can contain `B` while `B` contains `A`. Without the `seen`
    // guard the recursion never terminated and `getData()` blew the stack,
    // blanking the diagram. The collapse root itself is excluded so a back-edge
    // cannot hide the very node the collapse is supposed to draw.
    const collectDescendants = (sgId: string, ancestor: string, seen = new Set<string>()) => {
      if (seen.has(sgId)) {
        return;
      }
      seen.add(sgId);
      const sg = this.subGraphLookup.get(sgId);
      if (!sg) {
        return;
      }
      for (const childId of sg.nodes) {
        if (childId === ancestor || seen.has(childId)) {
          continue;
        }
        hiddenIds.add(childId);
        collapsedAncestorMap.set(childId, ancestor);
        // Recurse into child subgraphs
        collectDescendants(childId, ancestor, seen);
      }
    };
    for (const sg of subGraphs) {
      if (sg.metadata?.view === 'collapsed' && !hiddenIds.has(sg.id)) {
        collectDescendants(sg.id, sg.id);
      }
    }

    // Setup the subgraph data for adding nodes
    //
    // A parent assignment that closes a containment cycle (`A` contains `B`
    // and `B` contains `A`, which two cross-boundary edges are enough to
    // produce) is refused: graphlib rejects `setParent` on a cycle and the
    // whole diagram would fail to render. The first assignment wins and the
    // one that would close the loop is reported as a containment violation.
    const wouldCloseContainmentCycle = (childId: string, parentId: string) => {
      const seen = new Set<string>();
      let current: string | undefined = parentId;
      while (current !== undefined && !seen.has(current)) {
        if (current === childId) {
          return true;
        }
        seen.add(current);
        current = parentDB.get(current);
      }
      return false;
    };
    for (let i = subGraphs.length - 1; i >= 0; i--) {
      const subGraph = subGraphs[i];
      if (hiddenIds.has(subGraph.id)) {
        continue;
      }
      if (subGraph.nodes.length > 0) {
        subGraphDB.set(subGraph.id, true);
      }
      for (const id of subGraph.nodes) {
        if (wouldCloseContainmentCycle(id, subGraph.id)) {
          this.emitWarning(
            AgentflowWarning.CONTAINMENT_VIOLATION,
            `Container "${subGraph.id}" cannot contain "${id}" because "${id}" already contains it. The nesting that would close the loop is dropped.`,
            { nodeId: id }
          );
          continue;
        }
        parentDB.set(id, subGraph.id);
      }
    }

    // Data is setup, add the nodes
    for (let i = subGraphs.length - 1; i >= 0; i--) {
      const subGraph = subGraphs[i];
      if (hiddenIds.has(subGraph.id)) {
        continue;
      }

      const isCollapsed = subGraph.metadata?.view === 'collapsed';

      if (isCollapsed) {
        // Collapsed: render as a compact node with container-type styling and collapse indicator
        const collapsedMetadata = {
          ...subGraph.metadata,
          containerType: subGraph.type,
        };
        nodes.push({
          id: subGraph.id,
          label: subGraph.title,
          labelStyle: '',
          labelType: subGraph.labelType,
          parentId: parentDB.get(subGraph.id),
          padding: 8,
          cssCompiledStyles: this.getCompiledStyles(subGraph.classes),
          cssClasses: subGraph.classes.join(' '),
          shape: 'collapsedGroup',
          dir: subGraph.dir,
          isGroup: false,
          look: config.look,
          metadata: collapsedMetadata,
        });
      } else {
        nodes.push({
          id: subGraph.id,
          label: subGraph.title,
          labelStyle: '',
          labelType: subGraph.labelType,
          parentId: parentDB.get(subGraph.id),
          padding: 8,
          cssCompiledStyles: this.getCompiledStyles(subGraph.classes),
          cssClasses: subGraph.classes.join(' '),
          shape: SUBGRAPH_TYPE_TO_SHAPE[subGraph.type ?? 'flow'],
          dir: subGraph.dir,
          isGroup: true,
          look: config.look,
          metadata: subGraph.metadata,
        });
      }
    }

    const n = this.getVertices();
    n.forEach((vertex) => {
      // Skip vertices hidden by collapsed subgraphs
      if (hiddenIds.has(vertex.id)) {
        return;
      }
      this.addNodeFromVertex(vertex, nodes, parentDB, subGraphDB, config, config.look || 'classic');
    });

    const e = this.getEdges();
    e.forEach((rawEdge, index) => {
      // Redirect cross-boundary edges to the outermost visible collapsed
      // ancestor (issue #53). When two *distinct* endpoints collapse to the
      // same ancestor the edge would be a self-loop on the collapsed node and
      // is dropped instead. Genuine self-loops authored as `a --> a` are kept
      // (issue #58) — only collapse-induced self-loops are dropped.
      const startId = collapsedAncestorMap.get(rawEdge.start) ?? rawEdge.start;
      const endId = collapsedAncestorMap.get(rawEdge.end) ?? rawEdge.end;
      if (startId === endId && rawEdge.start !== rawEdge.end) {
        return;
      }
      const { arrowTypeStart, arrowTypeEnd } = this.destructEdgeType(rawEdge.type);
      const styles = [...(e.defaultStyle ?? [])];

      if (rawEdge.style) {
        styles.push(...rawEdge.style);
      }
      const edge: Edge = {
        id: getEdgeId(startId, endId, { counter: index, prefix: 'L' }, rawEdge.id),
        isUserDefinedId: rawEdge.isUserDefinedId,
        start: startId,
        end: endId,
        type: rawEdge.type ?? 'normal',
        label: rawEdge.text,
        labelType: rawEdge.labelType,
        labelpos: 'c',
        thickness: rawEdge.stroke,
        minlen: rawEdge.length,
        classes:
          rawEdge?.stroke === 'invisible'
            ? ''
            : 'edge-thickness-normal edge-pattern-solid flowchart-link',
        arrowTypeStart:
          rawEdge?.stroke === 'invisible' || rawEdge?.type === 'arrow_open'
            ? 'none'
            : arrowTypeStart,
        arrowTypeEnd:
          rawEdge?.stroke === 'invisible' || rawEdge?.type === 'arrow_open' ? 'none' : arrowTypeEnd,
        // Inert: `Edge.arrowheadStyle` is declared but never read by the
        // renderer. Kept at flowchart parity rather than theme-derived.
        arrowheadStyle: 'fill: #333',
        cssCompiledStyles: this.getCompiledStyles(rawEdge.classes),
        labelStyle: styles,
        style: styles,
        pattern: rawEdge.stroke,
        look: config.look,
        animate: rawEdge.animate,
        animation: rawEdge.animation,
        curve: rawEdge.interpolate || this.edges.defaultInterpolate || config.flowchart?.curve,
        // §5.3 edge metadata (e.g. `instruction`) — carried to the IR so
        // downstream tooling (dispatch compile) can read it (issue #62).
        metadata: rawEdge.metadata,
      };

      edges.push(edge);
    });

    // Shape validation runs here, not only at render time. `getDiagnostics()`
    // is built for editor tooling, and tooling calls `parse()` — it never
    // renders — so leaving the only two live diagnostics behind `renderer.draw`
    // meant `mermaid.parse(text)` followed by `getDiagnostics()` always
    // returned `[]`.
    normaliseNodeShapes(nodes, this);

    // Palette slots. Wired here rather than in `transformData` because this is the path the
    // renderer actually takes -- `getData()` calls `normaliseNodeShapes` directly, so a hook
    // added to `transformData` never runs on a real render.
    //
    // The kind comes from the vertex record, not from the resolved shape: a `connector` and a
    // `task` are both `roundedRect`, so reading it off the shape paints every connector as a
    // task.
    // Declaration order for the container counter. Neither `nodes` nor `subGraphs` carries
    // it: the loops above walk `subGraphs` in REVERSE, and `subGraphs` is itself in
    // completion order, because the grammar reduces a container when it closes and a
    // nested one therefore lands before its parent. Un-reversing would fix the flat case
    // and still get nesting wrong.
    //
    // A pre-order walk of the containment forest recovers source order: roots complete in
    // source order relative to each other, and a parent is always declared before the
    // children it holds. `flowDb` builds its `declarationIndex` the same way.
    const containerOrder = new Map<string, number>();
    const childContainers = new Map<string, string[]>();
    for (const sg of subGraphs) {
      const parent = parentDB.get(sg.id);
      if (parent !== undefined) {
        childContainers.set(parent, [...(childContainers.get(parent) ?? []), sg.id]);
      }
    }
    let nextContainer = 0;
    const walkContainers = (id: string) => {
      if (containerOrder.has(id)) {
        return; // a containment cycle was refused above; do not loop on its remnant
      }
      containerOrder.set(id, nextContainer++);
      for (const child of childContainers.get(id) ?? []) {
        walkContainers(child);
      }
    };
    for (const sg of subGraphs) {
      if (parentDB.get(sg.id) === undefined) {
        walkContainers(sg.id);
      }
    }

    const connectorIds = new Set(this.getConnectors().map((c) => c.id));
    assignColorSlots(
      nodes,
      (id) => {
        if (connectorIds.has(id)) {
          return 'connector';
        }
        const v = this.vertices.get(id);
        return v
          ? this.deriveVertexKind(v, resolveShapeAlias(v.type as string | undefined))
          : undefined;
      },
      containerOrder
    );

    return {
      nodes,
      edges,
      other: {},
      config,
      connectors: this.getConnectors(),
    };
  }

  public defaultConfig() {
    return defaultConfig.flowchart;
  }

  // ── Semantic-model projection (PR 3) ─────────────────────────────────
  //
  // `getSemanticModel()` returns a presentation-stripped view of the
  // diagram state for downstream tooling. Per the agentflow syntax specification §13
  // `view`, `classDef` / `class` / `style` / `linkStyle`, `icon`, `img`,
  // `w`, `h`, collapsed/expanded state, element mappings, and interactivity
  // bindings are presentation-only and MUST NOT influence semantic
  // interpretation — so none of them appear in the returned model. Fields
  // that carry meaning (ids, labels, shape, domain metadata, edge
  // arrow/stroke/label, subgraph membership, type/template declarations,
  // diagnostics) are kept.

  /**
   * The v0.8.1 §4 vertex kind for a parsed vertex.
   *
   * Extracted so the semantic model and `getData()`'s palette slots read the same rules
   * from one place. Kind is NOT recoverable from the resolved shape alone — a tool and a
   * task can both land on `roundedRect` — which is why this takes the vertex and not just
   * its shape.
   */
  private deriveVertexKind(v: FlowVertex, resolvedShape: string | undefined): VertexKind {
    if (this.isToolDefinition(v)) {
      return 'tool';
    }
    if (resolvedShape === 'hexagon' || resolvedShape === 'hex') {
      return 'action';
    }
    if (resolvedShape === 'lean-right' || resolvedShape === 'lean_right') {
      return 'input';
    }
    if (resolvedShape === 'lin-doc' || resolvedShape === 'lined-document') {
      return 'refdoc';
    }
    if (resolvedShape === 'diamond') {
      return 'decision';
    }
    return 'task';
  }

  public getSemanticModel(): AgentflowSemanticModel {
    // Run the post-parse validators so that the semantic export includes
    // up-to-date diagnostics.
    this.runPostParseValidators();
    // Subgraph ids sometimes also appear in `this.vertices` when metadata
    // (`@{...}`) is attached to a container id — the metadata-attachment
    // path creates a placeholder vertex record. Those are NOT semantic
    // vertices; they're container descriptors and the semantic model
    // exposes them via `subGraphs` instead.
    const subGraphIds = new Set(this.subGraphs.map((sg) => sg.id));
    const vertices: SemanticVertex[] = [];
    const connectors: SemanticConnector[] = [];
    for (const [id, v] of this.vertices) {
      if (subGraphIds.has(id)) {
        continue;
      }
      if (v.isConnector) {
        const connector: SemanticConnector = { id };
        if (v.text !== undefined && v.text !== id) {
          connector.title = v.text;
        }
        if (v.metadata && Object.keys(v.metadata).length > 0) {
          const meta: Record<string, unknown> = {};
          for (const [key, value] of Object.entries(v.metadata)) {
            if (SEMANTIC_METADATA_SKIP_KEYS.has(key)) {
              continue;
            }
            meta[key] = value;
          }
          if (Object.keys(meta).length > 0) {
            connector.metadata = meta;
          }
        }
        connectors.push(connector);
        continue;
      }
      const vertex: SemanticVertex = { id };
      if (v.text !== undefined) {
        vertex.label = v.text;
      }
      const resolvedShape = typeof v.type === 'string' ? resolveShapeAlias(v.type) : v.type;
      if (resolvedShape !== undefined) {
        vertex.shape = resolvedShape;
      }
      // Derived vertex kind per v0.8.1 §4.
      vertex.vertexKind = this.deriveVertexKind(v, resolvedShape);
      if (v.metadata && Object.keys(v.metadata).length > 0) {
        // Strip presentation-only keys from metadata passthrough.
        const meta: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(v.metadata)) {
          if (SEMANTIC_METADATA_SKIP_KEYS.has(key)) {
            continue;
          }
          meta[key] = value;
        }
        if (Object.keys(meta).length > 0) {
          vertex.metadata = meta;
        }
      }
      vertices.push(vertex);
    }

    const edges: SemanticEdge[] = this.edges.map((e) => {
      const edge: SemanticEdge = { start: e.start, end: e.end };
      if (e.id !== undefined) {
        edge.id = e.id;
      }
      if (typeof e.text === 'string' && e.text.length > 0) {
        edge.label = e.text;
      }
      if (e.type !== undefined) {
        edge.type = e.type;
      }
      if (e.stroke !== undefined) {
        edge.stroke = e.stroke;
      }
      if (e.edgeSemantic !== undefined) {
        edge.edgeSemantic = e.edgeSemantic;
      }
      if (e.length !== undefined) {
        edge.length = e.length;
      }
      if (e.metadata && Object.keys(e.metadata).length > 0) {
        edge.metadata = { ...e.metadata };
      }
      return edge;
    });

    const subGraphs: SemanticSubGraph[] = this.subGraphs.map((sg) => {
      const out: SemanticSubGraph = {
        id: sg.id,
        nodes: [...sg.nodes],
      };
      if (sg.type !== undefined) {
        out.type = sg.type;
      }
      if (sg.title !== undefined) {
        out.title = sg.title;
      }
      if (sg.dir !== undefined) {
        out.direction = sg.dir;
      }
      if (sg.metadata && Object.keys(sg.metadata).length > 0) {
        const meta: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(sg.metadata)) {
          if (SEMANTIC_METADATA_SKIP_KEYS.has(key)) {
            continue;
          }
          meta[key] = value;
        }
        if (Object.keys(meta).length > 0) {
          out.metadata = meta;
        }
      }
      return out;
    });

    const model: AgentflowSemanticModel = {
      vertices,
      edges,
      subGraphs,
      connectors,
      diagnostics: this.diagnostics,
    };
    if (this.direction !== undefined) {
      model.direction = this.direction;
    }
    return model;
  }

  // ── Element-mapping infrastructure (PR 2a) ────────────────────────────
  //
  // JISON action blocks call the `add*Mapping` methods alongside the
  // structural `add*` methods; see `agentflow.jison`. When a diagram DB
  // does not expose these methods the JISON guard `if (yy.addVertexMapping)`
  // simply skips them, so the mapping layer is opt-in and has no effect on
  // diagrams that don't consume positions.
  public setFrontmatterLineOffset(offset: number): void {
    this.frontmatterLineOffset = offset ?? 0;
  }

  private toElementPosition(loc: JisonLocation | undefined): ElementPosition {
    // JISON always passes a location object when location tracking is
    // enabled, but defend against a missing loc to keep the parser robust
    // in the face of future grammar rules that forget `@$`.
    const first_line = loc?.first_line ?? 0;
    const first_column = loc?.first_column ?? 0;
    const last_line = loc?.last_line ?? first_line;
    const last_column = loc?.last_column ?? first_column;
    const [startIndex, endIndex] = loc?.range ?? [0, 0];
    return {
      startLine: first_line + this.frontmatterLineOffset,
      startColumn: first_column,
      endLine: last_line + this.frontmatterLineOffset,
      endColumn: last_column,
      startIndex,
      endIndex,
    };
  }

  private pushMapping(
    id: string,
    type: AgentflowStatementType,
    loc: JisonLocation | undefined
  ): AgentflowElementMapping | undefined {
    if (!id) {
      return undefined;
    }
    const mapping: AgentflowElementMapping = {
      id,
      type,
      position: this.toElementPosition(loc),
    };
    this.elementMappings.push(mapping);
    return mapping;
  }

  public addVertexMapping(
    id: string,
    _text: unknown,
    shape: unknown,
    loc: JisonLocation | undefined
  ): void {
    const mapping = this.pushMapping(id, 'vertex', loc);
    // A bare `id` reference carries no shape (the bracketed declaration
    // forms all pass one). Remember it so a trailing `@{ ... }` block can
    // retype it to 'attachment' instead of leaving a second mapping that
    // looks like a redeclaration (issue #75).
    if (mapping && shape == null) {
      this.bareVertexMappings.add(mapping);
    }
  }

  /**
   * Extend a vertex's mapping end to cover a trailing inline metadata block
   * (`id["..."]@{ ... }`). The node declaration already pushed a mapping
   * spanning just the declaration; when the `@{ ... }` block reduces we widen
   * that mapping's end to the block's closing `}` so editor cursors inside the
   * block resolve to the node rather than the containing flow (issue #60).
   * `loc` is the `shapeData` symbol's location, so only the end moves — the
   * declaration start is preserved. Falls back to a fresh mapping if the node
   * has none yet.
   *
   * When the mapping being widened came from a bare `id` reference (no
   * label/shape brackets), the statement is a standalone attachment
   * (`id@{ ... }`) annotating an element declared elsewhere — retype it to
   * 'attachment' so consumers can tell it apart from a declaration
   * (issue #75).
   */
  public extendVertexMapping(id: string, loc: JisonLocation | undefined): void {
    if (!id || !loc) {
      return;
    }
    const end = this.toElementPosition(loc);
    for (let i = this.elementMappings.length - 1; i >= 0; i--) {
      const m = this.elementMappings[i];
      if (m.type === 'vertex' && m.id === id) {
        if (this.bareVertexMappings.has(m)) {
          m.type = 'attachment';
          this.bareVertexMappings.delete(m);
        }
        const extendsPastEnd =
          end.endLine > m.position.endLine ||
          (end.endLine === m.position.endLine && end.endColumn > m.position.endColumn);
        if (extendsPastEnd) {
          m.position.endLine = end.endLine;
          m.position.endColumn = end.endColumn;
          m.position.endIndex = end.endIndex;
        }
        return;
      }
    }
    // No vertex mapping to widen — the `@{ ... }` block stands alone, so
    // record it as an attachment spanning just the block.
    this.pushMapping(id, 'attachment', loc);
  }

  public addEdgeMapping(
    _fromStmt: unknown,
    toNodes: unknown,
    _link: unknown,
    loc: JisonLocation | undefined
  ): void {
    // JISON hands the edge's right-hand-side node list in; derive a stable
    // identifier from the destination node IDs joined by `>`. This matches
    // the fan-out shape (`A --> B & C` resolves to one edge statement with
    // two destinations) and keeps the mapping lookup intuitive.
    const ids = Array.isArray(toNodes)
      ? toNodes.map((n) => (typeof n === 'string' ? n : (n?.id ?? ''))).filter(Boolean)
      : [];
    const edgeId = ids.length > 0 ? ids.join('>') : 'edge';
    this.pushMapping(edgeId, 'edge', loc);
  }

  public addSubgraphMapping(
    _id: unknown,
    _title: unknown,
    startLoc: JisonLocation | undefined,
    endLoc: JisonLocation | undefined
  ): void {
    const id = (typeof _id === 'string' ? _id : (_id as { text?: string } | undefined)?.text) ?? '';
    const start = this.toElementPosition(startLoc);
    const end = endLoc ? this.toElementPosition(endLoc) : start;
    if (!id) {
      return;
    }
    this.elementMappings.push({
      id,
      type: 'subgraph',
      // Range spans from the container keyword to its `end` — startLine from
      // the opener, endLine from the closer.
      position: {
        startLine: start.startLine,
        startColumn: start.startColumn,
        endLine: end.endLine,
        endColumn: end.endColumn,
        startIndex: start.startIndex,
        endIndex: end.endIndex,
      },
    });
  }

  public addConnectorMapping(
    textObj: FlowText | undefined,
    _titleObj: FlowText | undefined,
    startLoc: JisonLocation | undefined,
    endLoc: JisonLocation | undefined
  ): void {
    const id = textObj?.text?.trim() ?? '';
    if (!id) {
      return;
    }
    const start = this.toElementPosition(startLoc);
    const end = endLoc ? this.toElementPosition(endLoc) : start;
    this.elementMappings.push({
      id,
      type: 'connector',
      position: {
        startLine: start.startLine,
        startColumn: start.startColumn,
        endLine: end.endLine,
        endColumn: end.endColumn,
        startIndex: start.startIndex,
        endIndex: end.endIndex,
      },
    });
  }

  public getElementMappings(): readonly AgentflowElementMapping[] {
    // Copy, for the same reason as `getDiagnostics()`.
    return [...this.elementMappings];
  }

  public getElementById(id: string): AgentflowElementMapping | undefined {
    return this.elementMappings.find((m) => m.id === id);
  }

  public getElementsOnLine(line: number): AgentflowElementMapping[] {
    return this.elementMappings.filter(
      (m) => line >= m.position.startLine && line <= m.position.endLine
    );
  }

  public getElementAtPosition(line: number, column: number): AgentflowElementMapping | undefined {
    // Return the innermost (smallest span) match so nested subgraphs resolve
    // to the deepest container that contains the point rather than the
    // outermost one.
    const candidates = this.elementMappings.filter((m) => {
      const { startLine, startColumn, endLine, endColumn } = m.position;
      if (line < startLine || line > endLine) {
        return false;
      }
      if (line === startLine && column < startColumn) {
        return false;
      }
      if (line === endLine && column > endColumn) {
        return false;
      }
      return true;
    });
    if (candidates.length === 0) {
      return undefined;
    }
    // Compare [lineSpan, columnSpan] lexicographically. Folding the two into
    // `lineSpan * 1000 + columnSpan` mis-ranked lines wider than 1000 columns,
    // and the column term goes negative whenever a span crosses a line break.
    const span = ({ position }: AgentflowElementMapping): [number, number] => [
      position.endLine - position.startLine,
      position.endColumn - position.startColumn,
    ];
    return candidates.reduce((smallest, cur) => {
      const [smallLines, smallColumns] = span(smallest);
      const [curLines, curColumns] = span(cur);
      if (curLines !== smallLines) {
        return curLines < smallLines ? cur : smallest;
      }
      return curColumns < smallColumns ? cur : smallest;
    });
  }

  public getMappingStats(): {
    vertices: number;
    edges: number;
    subgraphs: number;
    connectors: number;
    attachments: number;
    totalElements: number;
  } {
    let vertices = 0;
    let edges = 0;
    let subgraphs = 0;
    let connectors = 0;
    let attachments = 0;
    for (const m of this.elementMappings) {
      if (m.type === 'vertex') {
        vertices++;
      } else if (m.type === 'edge') {
        edges++;
      } else if (m.type === 'subgraph') {
        subgraphs++;
      } else if (m.type === 'connector') {
        connectors++;
      } else if (m.type === 'attachment') {
        attachments++;
      }
    }
    return {
      vertices,
      edges,
      subgraphs,
      connectors,
      attachments,
      totalElements: this.elementMappings.length,
    };
  }

  // ── Diagnostics (PR 2b) ────────────────────────────────────────────────
  //
  // `emitWarning` (and its error-severity counterpart `emitError`) record a
  // structured diagnostic and also fire `log.warn` so humans see the
  // message in the console. When the caller supplies a `nodeId` or
  // `edgeId`, the position is looked up through the element-mapping layer
  // added in PR 2a — so the diagnostic carries enough context for an
  // editor to highlight the offending element.

  private emitDiagnostic(
    id: AgentflowWarningId,
    severity: 'warning' | 'error',
    message: string,
    ctx?: AgentflowDiagnosticContext
  ): void {
    const anchorId = ctx?.nodeId ?? ctx?.edgeId;
    const mapping = anchorId ? this.getElementById(anchorId) : undefined;
    const diagnostic: AgentflowDiagnostic = {
      id,
      severity,
      message,
      ...(ctx?.nodeId ? { nodeId: ctx.nodeId } : {}),
      ...(ctx?.edgeId && !ctx?.nodeId ? { edgeId: ctx.edgeId } : {}),
      ...(mapping ? { position: mapping.position } : {}),
    };
    // `getData()` is idempotent and may be called more than once (parse-then-
    // render, or tooling probing the DB). Re-emitting the same finding each
    // time would inflate the diagnostics list, so record each one once.
    const alreadyRecorded = this.diagnostics.some(
      (existing) =>
        existing.id === diagnostic.id &&
        existing.severity === diagnostic.severity &&
        existing.message === diagnostic.message &&
        existing.nodeId === diagnostic.nodeId &&
        existing.edgeId === diagnostic.edgeId
    );
    if (alreadyRecorded) {
      return;
    }
    this.diagnostics.push(diagnostic);
    const formatted = `agentflow[${id}]: ${message}`;
    if (severity === 'error') {
      log.error(formatted);
    } else {
      log.warn(formatted);
    }
  }

  public emitWarning(
    id: AgentflowWarningId,
    message: string,
    ctx?: AgentflowDiagnosticContext
  ): void {
    this.emitDiagnostic(id, 'warning', message, ctx);
  }

  public emitError(
    id: AgentflowWarningId,
    message: string,
    ctx?: AgentflowDiagnosticContext
  ): void {
    this.emitDiagnostic(id, 'error', message, ctx);
  }

  /**
   * `readonly` is erased at runtime, so hand back a copy — this is a
   * consumer-facing accessor and a caller sorting the result in place would
   * otherwise reorder the DB's own list.
   */
  public getDiagnostics(): readonly AgentflowDiagnostic[] {
    return [...this.diagnostics];
  }

  public setAccTitle = setAccTitle;
  public setAccDescription = setAccDescription;
  public setDiagramTitle = setDiagramTitle;
  public getAccTitle = getAccTitle;
  public getAccDescription = getAccDescription;
  public getDiagramTitle = getDiagramTitle;
}
